import { Lender } from '@1delta/lender-registry'

/**
 * Per-market pause state for the Compound V2 forks whose lens does not carry
 * it.
 *
 * Venus (`pausedActions` bitmap), Moonwell (`mintPaused`/`borrowPaused`),
 * Benqi and Kinetic all publish pause flags through their own lens, so their
 * readers fill `pausedActions` from real data. The plain `cTokenMetadata`
 * lenses (Compound V2 OG, Cream, Flux, WePiggy, Gamma, Capy, Lodestar, Tender)
 * return no pause field at all, and the readers used to hard-code
 * "nothing is paused" — which advertised the whole of Compound V2 mainnet
 * (19/19 markets mint- AND borrow-paused, ~$98M TVL) as an open venue whose
 * every deposit and borrow reverts on-chain.
 *
 * For those forks the flags are read straight off the Comptroller, two calls
 * per cToken, appended after the two global comptroller reads. See
 * `publicCallBuild.ts` for the exact layout and `README.md` for the counts.
 */
export const COMPTROLLER_PAUSE_READ_LENDERS: string[] = [
  Lender.COMPOUND_V2,
  Lender.CREAM_FINANCE,
  Lender.FLUX_FINANCE,
  Lender.WE_PIGGY,
  Lender.GAMMA,
  Lender.CAPY_FI,
  Lender.LODESTAR,
  Lender.TENDER,
  // Long-tail forks on the shared lenses — same plain `cTokenMetadata`, same
  // missing pause field. Their Comptrollers DO answer both guardian getters
  // (verified 2026-08-24), so the live read resolves every market.
  Lender.DEMETER,
  Lender.LANDER,
  Lender.FILDA,
  Lender.SOLIDLIZARD,
  Lender.IRON_BANK,
  Lender.MENDI,
  Lender.PAXO,
  Lender.SONNE,
  Lender.ASO_FINANCE,
  Lender.BLUME,
  Lender.KAWA,
  Lender.XPERT,
  Lender.KEOM,
  Lender.REACTOR_FUSION,
]

/**
 * True when this lender's markets need the per-market Comptroller pause reads
 * (i.e. its lens does not surface them). Kept in one place because the builder
 * and the parser must agree on the call count exactly — the parser bails with
 * `undefined` on a mismatch, which silently drops the whole lender.
 */
export const usesComptrollerPauseReads = (lender: string): boolean =>
  COMPTROLLER_PAUSE_READ_LENDERS.includes(lender)

/** Resolved pause state for one market; `undefined` means NOT KNOWN. */
export interface MarketPauseFlags {
  mintPaused?: boolean
  borrowPaused?: boolean
}

/**
 * Decode one guardian read.
 *
 * A reverted call surfaces as the `'0x'` placeholder — either the fork's
 * Comptroller has no such getter (WePiggy's has neither, verified against its
 * deployed implementation) or the RPC dropped the call. Both are reported as
 * `undefined` = unknown rather than `false`, so an unknown never masquerades
 * as "open". The converter leaves the reader's own value in place for those,
 * which keeps today's behaviour instead of blanking a lender on a hiccup.
 */
export const parsePausedFlag = (raw: any): boolean | undefined =>
  typeof raw === 'boolean' ? raw : undefined

/**
 * Resolve one market's pause state: the live Comptroller read wins, and where
 * it came back unknown we fall back to the value published on the token entry
 * by `lender-metadata`.
 *
 * That static leg exists for exactly one shape — a fork whose Comptroller has
 * no guardian GETTER, so no runtime read can reach the flag (WePiggy: the
 * mappings are not public, though `_setMintPaused` and `pauseGuardian()` are
 * both in its deployed implementation, and 4 of its 10 Ethereum markets are in
 * fact paused). The generator resolves those off-chain by simulating
 * `mintAllowed`/`borrowAllowed` and decoding the revert reason, which costs the
 * fetcher nothing per request.
 *
 * Returns `undefined` when neither leg knows anything, which leaves the
 * reader's own value untouched.
 */
export const resolveMarketPause = (
  token?: MarketPauseFlags,
  live?: MarketPauseFlags,
): MarketPauseFlags | undefined => {
  const mintPaused = live?.mintPaused ?? token?.mintPaused
  const borrowPaused = live?.borrowPaused ?? token?.borrowPaused
  if (mintPaused === undefined && borrowPaused === undefined) return undefined
  return { mintPaused, borrowPaused }
}
